import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  ListNotesResponseSchema,
  NoteResponseSchema,
} from "@forgepoint/shared";
import type {
  Note,
  NoteEntityType,
  CreateNoteParams,
} from "@forgepoint/shared";

interface LoadingState {
  isLoading: boolean;
  error: string | null;
}

interface CreateLoadingState {
  isCreating: boolean;
  createError: string | null;
}

/**
 * Returns the default loading state for an entity.
 * @returns A LoadingState with isLoading false and no error
 */
const getDefaultLoadingState = (): LoadingState => ({
  isLoading: false,
  error: null,
});

/**
 * Returns the default create loading state for an entity.
 * @returns A CreateLoadingState with isCreating false and no error
 */
const getDefaultCreateLoadingState = (): CreateLoadingState => ({
  isCreating: false,
  createError: null,
});

/**
 * Builds a unique key for entity-scoped note storage.
 * @param entityType - The type of entity
 * @param entityId - The entity ID
 * @returns A composite key string
 */
const buildEntityKey = (entityType: NoteEntityType, entityId: string): string =>
  `${entityType}:${entityId}`;

interface NotesStore {
  notes: Map<string, Note>;
  notesByEntity: Map<string, string[]>;
  loadingStates: Map<string, LoadingState>;
  createLoadingStates: Map<string, CreateLoadingState>;

  fetchNotes: (entityType: NoteEntityType, entityId: string) => Promise<void>;
  createNote: (params: CreateNoteParams) => Promise<Note | null>;
  updateNote: (
    noteId: string,
    content: string,
    assetIds?: string[],
  ) => Promise<Note | null>;
  deleteNote: (
    noteId: string,
    entityType: NoteEntityType,
    entityId: string,
  ) => Promise<boolean>;
  getNotesForEntity: (entityType: NoteEntityType, entityId: string) => Note[];
  getLoadingState: (
    entityType: NoteEntityType,
    entityId: string,
  ) => LoadingState;
  getCreateLoadingState: (
    entityType: NoteEntityType,
    entityId: string,
  ) => CreateLoadingState;
}

export const useNotesStore = create<NotesStore>((set, get) => ({
  notes: new Map(),
  notesByEntity: new Map(),
  loadingStates: new Map(),
  createLoadingStates: new Map(),

  fetchNotes: async (entityType: NoteEntityType, entityId: string) => {
    const entityKey = buildEntityKey(entityType, entityId);

    set((state) => {
      const newLoadingStates = new Map(state.loadingStates);
      newLoadingStates.set(entityKey, { isLoading: true, error: null });
      return { loadingStates: newLoadingStates };
    });

    try {
      const response = await fetchApi(
        `notes?entityType=${entityType}&entityId=${entityId}`,
        "GET",
      );

      if (response.ok && response.data) {
        const parsed = ListNotesResponseSchema.parse(response.data);

        set((state) => {
          const newNotes = new Map(state.notes);
          const newNotesByEntity = new Map(state.notesByEntity);
          const newLoadingStates = new Map(state.loadingStates);

          const noteIds = parsed.notes.map((note) => {
            newNotes.set(note.id, note);
            return note.id;
          });

          newNotesByEntity.set(entityKey, noteIds);
          newLoadingStates.set(entityKey, { isLoading: false, error: null });

          return {
            notes: newNotes,
            notesByEntity: newNotesByEntity,
            loadingStates: newLoadingStates,
          };
        });
        return;
      }

      set((state) => {
        const newLoadingStates = new Map(state.loadingStates);
        newLoadingStates.set(entityKey, {
          isLoading: false,
          error: "Failed to fetch notes",
        });
        return { loadingStates: newLoadingStates };
      });
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const newLoadingStates = new Map(state.loadingStates);
        newLoadingStates.set(entityKey, {
          isLoading: false,
          error: "Failed to fetch notes",
        });
        return { loadingStates: newLoadingStates };
      });
    }
  },

  createNote: async (params: CreateNoteParams) => {
    const entityKey = buildEntityKey(params.entityType, params.entityId);

    set((state) => {
      const newCreateLoadingStates = new Map(state.createLoadingStates);
      newCreateLoadingStates.set(entityKey, {
        isCreating: true,
        createError: null,
      });
      return { createLoadingStates: newCreateLoadingStates };
    });

    try {
      const response = await fetchApi("notes", "POST", { body: params });

      if (response.ok && response.data) {
        const parsed = NoteResponseSchema.parse(response.data);
        const note = parsed.note;

        set((state) => {
          const newNotes = new Map(state.notes);
          const newNotesByEntity = new Map(state.notesByEntity);
          const newCreateLoadingStates = new Map(state.createLoadingStates);

          newNotes.set(note.id, note);
          const existingIds = newNotesByEntity.get(entityKey) || [];
          newNotesByEntity.set(entityKey, [note.id, ...existingIds]);
          newCreateLoadingStates.set(entityKey, {
            isCreating: false,
            createError: null,
          });

          return {
            notes: newNotes,
            notesByEntity: newNotesByEntity,
            createLoadingStates: newCreateLoadingStates,
          };
        });

        return note;
      }

      set((state) => {
        const newCreateLoadingStates = new Map(state.createLoadingStates);
        newCreateLoadingStates.set(entityKey, {
          isCreating: false,
          createError: "Failed to create note",
        });
        return { createLoadingStates: newCreateLoadingStates };
      });
      return null;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const newCreateLoadingStates = new Map(state.createLoadingStates);
        newCreateLoadingStates.set(entityKey, {
          isCreating: false,
          createError: "Failed to create note",
        });
        return { createLoadingStates: newCreateLoadingStates };
      });
      return null;
    }
  },

  updateNote: async (noteId: string, content: string, assetIds?: string[]) => {
    try {
      const body: { content: string; assetIds?: string[] } = { content };
      if (assetIds !== undefined) {
        body.assetIds = assetIds;
      }
      const response = await fetchApi(`notes/${noteId}`, "PUT", {
        body,
      });

      if (response.ok && response.data) {
        const parsed = NoteResponseSchema.parse(response.data);
        const note = parsed.note;

        set((state) => {
          const newNotes = new Map(state.notes);
          newNotes.set(note.id, note);
          return { notes: newNotes };
        });

        return note;
      }

      return null;
    } catch (error) {
      captureException(error as Error);
      return null;
    }
  },

  deleteNote: async (
    noteId: string,
    entityType: NoteEntityType,
    entityId: string,
  ) => {
    try {
      const response = await fetchApi(`notes/${noteId}`, "DELETE");

      if (response.ok) {
        const entityKey = buildEntityKey(entityType, entityId);

        set((state) => {
          const newNotes = new Map(state.notes);
          const newNotesByEntity = new Map(state.notesByEntity);

          newNotes.delete(noteId);
          const existingIds = newNotesByEntity.get(entityKey) || [];
          newNotesByEntity.set(
            entityKey,
            existingIds.filter((id) => id !== noteId),
          );

          return { notes: newNotes, notesByEntity: newNotesByEntity };
        });

        return true;
      }

      return false;
    } catch (error) {
      captureException(error as Error);
      return false;
    }
  },

  getNotesForEntity: (entityType: NoteEntityType, entityId: string) => {
    const entityKey = buildEntityKey(entityType, entityId);
    const noteIds = get().notesByEntity.get(entityKey) || [];
    const notes = get().notes;

    return noteIds
      .map((id) => notes.get(id))
      .filter((note): note is Note => note !== undefined);
  },

  getLoadingState: (entityType: NoteEntityType, entityId: string) => {
    const entityKey = buildEntityKey(entityType, entityId);
    return get().loadingStates.get(entityKey) || getDefaultLoadingState();
  },

  getCreateLoadingState: (entityType: NoteEntityType, entityId: string) => {
    const entityKey = buildEntityKey(entityType, entityId);
    return (
      get().createLoadingStates.get(entityKey) || getDefaultCreateLoadingState()
    );
  },
}));
